import React, { Component } from 'react';
import { localize } from 'react-localize-redux';
import { colors } from 'helpers/ColorHelper';
import { MdPriorityHigh as HighPriorityIcon } from 'react-icons/md';
import { toFriendlyDateString, daysFromNow, toDate } from 'helpers/Formatter';
import VacationIcon from '@material-ui/icons/GolfCourse';
import { toSnakeCase } from 'helpers/utilities';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';
import { getTranslatedString } from 'helpers/i18n';
import styled from 'styled-components';

const StatusFlagSpan = styled.span`
  font-size: 90%;
`;

export const MaintenanceTicketPriority = ({ priority }) => (
  <span>
    {priority === 'High' ? (
      <span style={{ marginLeft: '-6px' }}>
        <HighPriorityIcon size={14} style={{ verticalAlign: 'text-bottom', color: colors.RED }} />
        {getTranslatedString('landlordPortal.landlordTicketScheduler.high')}{' '}
      </span>
    ) : priority === 'Emergency' ? (
      <span style={{ marginLeft: '-6px' }}>
        <HighPriorityIcon
          size={14}
          style={{
            verticalAlign: 'text-bottom',
            color: colors.RED,
            marginRight: '-10px',
          }}
        />
        <HighPriorityIcon
          size={14}
          style={{
            verticalAlign: 'text-bottom',
            color: colors.RED,
            marginRight: '-10px',
          }}
        />
        <HighPriorityIcon size={14} style={{ verticalAlign: 'text-bottom', color: colors.RED }} />
        {getTranslatedString('landlordPortal.landlordTicketScheduler.emergency')}{' '}
      </span>
    ) : (
      getTranslatedString('landlordPortal.landlordTicketScheduler.normal')
    )}
  </span>
);

export const MaintenanceAssetPriority = ({ priority }) => {
  return <span> {priority} </span>;
};

export const ExpenseStatus = ({ status, style }) => {
  const statusMap = {
    Paid: 'paid',
    Outstanding: 'outstanding',
  };
  return (
    <span style={style} className={`ticket_status_label_title small ${statusMap[status]}`}>
      {status}
    </span>
  );
};

export const MaintenanceTemplateStatus = ({ status, style }) => {
  const templateStatus = status === true ? 'Active' : 'Inactive';

  const statusMap = {
    Inactive: 'inactive',
    Active: 'closed',
  };
  return (
    <span style={style} className={`ticket_status_label_title small ${statusMap[templateStatus]}`}>
      {templateStatus}
    </span>
  );
};

export const VisitorStatus = ({ status, style }) => {
  const statusMap = {
    pending_arrival: 'Pending Arrival',
    checked_in: 'Checked In',
    checked_out: 'Checked Out',
    stale: 'Stale',
  };
  return (
    <span style={style} className={`ticket_status_label_title small ${status}`}>
      {statusMap[status]}
    </span>
  );
};

export const ApplicationStatus = ({ status, style, className = 'ticket_status_label_title' }) => {
  const statusMap = {
    payment_received: 'Payment Received',
    application_denied: 'Denied',
    pending_tenant_signature: 'Lease Sent',
    pending_lease_approval: 'Pending Lease Approval',
    pending_unit_assignment: 'Pending Unit Assignment',
    create_lease: 'Create Lease',
    pending_landlord_signature: 'Sign Lease',
    pending_application_submission: 'Pending Application',
    pending_all_tenants: 'Pending',
    cancelled: 'Cancelled',
    flagged: 'Flagged',
    pending: 'Pending',
    approved: 'Approved',
    pending_approval: 'Pending Approval',
    pending_payment: 'Pending Payment',
    leased_to_other_applicant: 'Leased to Other',
    unit_reserved_for_other: 'Reserved for Other',
  };
  return (
    <span style={style} className={`${className} small ${status}`}>
      {statusMap[status]}
    </span>
  );
};

export const CotenantStatus = ({ status, style }) => {
  const statusMap = {
    applied: 'Applied',
    pending: 'Pending',
    denied: 'Denied',
    approved: 'Approved',
    signed_lease: 'Signed Lease',
    flagged: 'Flagged',
    bypassed: 'Bypassed Application',
    bypassed_signature: 'Bypassed Signature',
    missing_email: 'Missing Email',
  };
  return (
    <StatusFlagSpan style={style} className={`ticket_status_label_title small ${status}`}>
      {statusMap[status]}
    </StatusFlagSpan>
  );
};

export const PortalAccessibilityFlag = ({ style, portalAccess, isAgentYuhu }) => {
  if (portalAccess) return null;
  if (!isAgentYuhu) return null;
  return (
    <StatusFlagSpan style={style} className={`ticket_status_label_title small unhappytenant`}>
      {getTranslatedString('landlordPortal.statusHelper.portalNotAccessible')}
    </StatusFlagSpan>
  );
};

export const TenantAutopayStatus = ({ tenantDetails, style = {} }) => {
  let statusStyle = 'autopay off';
  let status = getTranslatedString('landlordPortal.statusHelper.autopayOff');
  const isBoardwalk = hostIsBoardwalk();
  if (tenantDetails.autopay_active && !isBoardwalk) {
    statusStyle = 'autopay on';
    status = getTranslatedString('landlordPortal.statusHelper.autopayOn');
  } else if (!tenantDetails.payments_active) {
    statusStyle = 'autopay disabled';
    status = getTranslatedString('landlordPortal.statusHelper.autopayDisabled');
  }
  if (
    isBoardwalk &&
    !!tenantDetails.external_autopay_status &&
    tenantDetails.external_autopay_is_setup
  ) {
    if (tenantDetails.external_autopay_status === 'rejected') {
      statusStyle = 'autopay off';
      status = getTranslatedString('landlordPortal.statusHelper.autopayOff');
    } else if (tenantDetails.external_autopay_status === 'pending') {
      statusStyle = 'autopay pending';
      status = getTranslatedString('landlordPortal.statusHelper.autopayPending');
    } else if (tenantDetails.external_autopay_status === 'approved') {
      statusStyle = 'autopay on';
      status = getTranslatedString('landlordPortal.statusHelper.autopayOn');
    } else if (tenantDetails.external_autopay_status === 'cancelled') {
      statusStyle = 'autopay off';
      status = getTranslatedString('landlordPortal.statusHelper.autopayCancelled');
    }
  }

  return (
    <span style={style} className={`ticket_status_label_title small ${statusStyle}`}>
      {status}
    </span>
  );
};

export const ProspectStatus = ({ status, style = {} }) => {
  const statusMap = {
    booked_showing: 'booked showing',
  };

  return (
    <span style={style} className={`ticket_status_label_title small ${status}`}>
      {statusMap[status]}
    </span>
  );
};

export const TenantStatus = ({ tenantDetails, style = {} }) => {
  let status = tenantDetails.status;
  if (!tenantDetails.email) {
    status = 'missing_email';
  }

  const statusMap = {
    not_invited: getTranslatedString('landlordPortal.statusHelper.notInvited'),
    inactive: getTranslatedString('landlordPortal.statusHelper.inactive'),
    active: getTranslatedString('landlordPortal.statusHelper.active'),
    // missing_email: 'missing email', TODO: this is a temporary measure, decide if we want to remove 'missing_email' completely
    missing_email: getTranslatedString('landlordPortal.statusHelper.notInvited'),
    invited: getTranslatedString('landlordPortal.statusHelper.invited'),
    inviting: getTranslatedString('landlordPortal.statusHelper.inviting'),
    existing_tenant: getTranslatedString('landlordPortal.statusHelper.existingResident'),
  };

  return (
    <span
      style={style}
      className={`ticket_status_label_title small ${
        status === 'existing_tenant' ? 'not_invited' : status
      }`}
    >
      {statusMap[status]}
    </span>
  );
};

export const ShowingStatus = ({ status, style = {} }) => {
  const statusMap = {
    cancelled: 'cancelled',
    no_show: 'no show',
    assign: 'assign',
    complete: 'complete',
    upcoming: 'upcoming',
  };

  return (
    <span style={style} className={`ticket_status_label_title small ${status || 'upcoming'}`}>
      {statusMap[status]}
    </span>
  );
};

export const RentRollStatusMapper = (leaseDetails) => {
  let status = 'occupied';
  if (leaseDetails.cancelled) {
    status = 'cancelled';
  } else if (leaseDetails.expired) {
    status = 'expired';
  } else if (leaseDetails.is_renewing) {
    status = 'renewing';
  } else if (leaseDetails.renewed) {
    status = 'renewed';
  } else if (leaseDetails.is_renewal && toDate(leaseDetails.early_move_in_date) > daysFromNow()) {
    status = 'is_renewal';
  } else if (
    !leaseDetails.move_in &&
    leaseDetails.early_move_in_date &&
    toDate(leaseDetails.early_move_in_date) > daysFromNow()
  ) {
    status = 'moving_in';
  } else if (
    !leaseDetails.move_in &&
    leaseDetails.early_move_in_date &&
    toDate(leaseDetails.early_move_in_date) < daysFromNow()
  ) {
    status = 'late_move_in';
  } else if (leaseDetails.move_out_date) {
    status = 'moving_out';
  } else if (!leaseDetails.cotenant_full_names) {
    status = 'vacant';
  } else if (leaseDetails.month_to_month) {
    status = 'month_to_month';
  }

  const statusMap = {
    vacant: getTranslatedString('landlordPortal.rentRollStatusMapper.vacant'),
    occupied: getTranslatedString('landlordPortal.rentRollStatusMapper.occupied'),
    expired: getTranslatedString('landlordPortal.rentRollStatusMapper.expired'),
    renewing: getTranslatedString('landlordPortal.rentRollStatusMapper.renewalInProgress'),
    cancelled: getTranslatedString('landlordPortal.rentRollStatusMapper.cancelled'),
    moving_out: getTranslatedString('landlordPortal.rentRollStatusMapper.moveOut', {
      date: toFriendlyDateString(leaseDetails.move_out_date),
    }),
    renewed: getTranslatedString('landlordPortal.rentRollStatusMapper.renewed'),
    is_renewal: getTranslatedString('landlordPortal.rentRollStatusMapper.renewal'),
    renewal_upcoming: getTranslatedString('landlordPortal.rentRollStatusMapper.renewBefore', {
      date: toFriendlyDateString(daysFromNow(-90, toDate(leaseDetails.lease_end_date))),
    }),
    moving_in: getTranslatedString('landlordPortal.rentRollStatusMapper.moveIn', {
      date: toFriendlyDateString(leaseDetails.early_move_in_date),
    }),
    late_move_in: getTranslatedString('landlordPortal.rentRollStatusMapper.lateMoveIn', {
      date: toFriendlyDateString(leaseDetails.early_move_in_date),
    }),
    month_to_month: getTranslatedString('landlordPortal.rentRollStatusMapper.monthToMonth'),
  };

  return { status, statusMap };
};

export const RentRollStatus = ({
  leaseDetails,
  style,
  isMobile = false,
  className = 'ticket_status_label_title',
}) => {
  const statusMapper = RentRollStatusMapper(leaseDetails);
  const status = statusMapper.status;
  const statusMap = statusMapper.statusMap;
  className = `${className} small ${status}`;
  if (isMobile) {
    className = `${className} mobile ${status}`;
  }

  return (
    <span style={style} className={className}>
      {statusMap[status]}
    </span>
  );
};

export const ParcelStatus = ({ parcelDetails, hasMultiple, style, isMobile }) => {
  let parcelStatus = 'Received';
  let statusIcon = <span />;
  if (parcelDetails.released_at) {
    parcelStatus = 'Released';
  } else if (parcelDetails.vacation) {
    parcelStatus = 'Vacation';
    statusIcon = (
      <VacationIcon style={{ fontSize: '10px', marginRight: '3px', verticalAlign: 'baseline' }} />
    );
  } else if (hasMultiple) {
    parcelStatus = 'Multiple';
  }

  let className = `ticket_status_label_rent_roll small ${parcelStatus}`;
  if (isMobile) {
    className = `ticket_status_label_rent_roll mobile ${parcelStatus}`;
  }
  return (
    <span style={style} className={className}>
      {statusIcon}
      {parcelStatus}
    </span>
  );
};

export const PaymentStatus = localize()(
  class PaymentStatus extends Component {
    render() {
      const statusMap = {
        NSF: this.props.translate('paymentStatus.nsf'),
        Late: this.props.translate('paymentStatus.late'),
        Autopay: this.props.translate('paymentStatus.autopay'),
        Paid: this.props.translate('paymentStatus.paid'),
        Waived: this.props.translate('paymentStatus.waived'),
        Pending: this.props.translate('paymentStatus.pending'),
      };

      return (
        <span
          style={this.props.style}
          className={`ticket_status_label_title small ${this.props.status || 'Pending'}`}
        >
          {statusMap[this.props.status] || this.props.translate('paymentStatus.pending')}
        </span>
      );
    }
  }
);

export const AmenityStatus = localize()(({ amenity, translate, style = {} }) => {
  const { offline_status, offline_ends_at } = amenity;
  const now = new Date();
  const isOnline =
    !offline_status ||
    (offline_ends_at && now > offline_ends_at && now.getUTCDate() > offline_ends_at.getUTCDate());
  const statusVal = isOnline ? 'online' : 'offline';
  return (
    <span style={style} className={`ticket_status_label_title small ${statusVal}`}>
      {translate(`landlordPortal.amenities.form.${statusVal}`)}
    </span>
  );
});

export const UserStatus = ({ user, style = {} }) => {
  let className = 'pending';
  if (user.status === 'Invited') {
    className = 'invited';
  } else if (user.status === 'Confirmed') {
    className = 'confirmed';
  } else if (user.status === 'Deactivated') {
    className = 'deactivated';
  } else if (user.status === 'Not Invited') {
    className = 'not_invited';
  }

  return (
    <span style={style} className={`ticket_status_label_title small ${className}`}>
      {getTranslatedString(`landlordPortal.users.apiStatus.${className}`)}
    </span>
  );
};

export const NoticeStatus = ({ notice, style = {} }) => {
  const statusInSnakeCase = toSnakeCase(notice.status);
  const className = 'notice_' + statusInSnakeCase;
  return (
    <span style={style} className={`ticket_status_label_title small ${className}`}>
      {notice.status}
    </span>
  );
};

export const AdditionalOccupantStatus = ({ occupant }) => {
  const MovedOutTag = styled.span`
    padding: 2px 5px 2px 5px;
    position: relative;
    top: 5px;
    color: #fff !important;
    border-radius: 4px;
    vertical-align: super;
    font-size: 9px;
    font-family: Circular;
    margin-left: 7px;
    background: #ff3434;
  `;
  if (occupant.moved_out) return <MovedOutTag>Moved Out</MovedOutTag>;
  else return null;
};
